import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { palette, Icon } from '@m12s/component-library';
import routes from 'lib/utils/routes';
import styled, { keyframes } from 'styled-components';
import { zLayer } from 'lib/utils/styled';
import { falTimes, plus } from 'lib/icons';

import { getMachine } from 'lib/selectors/getMachine';
import { getCurrentWorkOrderOp } from 'lib/selectors/getCurrentWorkOrderOp';
import { getLatestJob } from 'lib/selectors/getLatestJob';
import { getLatestActivityMode } from 'lib/selectors/getLatestActivityMode';
import { getIsSetupModeActive } from 'lib/selectors/getIsSetupModeActive';
import { getMaterialPickingEnabled } from 'lib/selectors/getMaterialPickingEnabled';
import { getIsAPMEnabled } from 'lib/selectors/getIsAPMEnabled';
import { getIsApmERPJobFlow } from 'lib/selectors/getIsApmERPJobFlow';

import {
  DRAWER_TOP_INSET,
  DRAWER_TOP_RADIUS,
  DRAWER_SHADOW,
} from 'components/MMDrawer';
import { JobDocsFooter } from 'components/JobDocsFooter';
import { useJobDocsIframe } from 'hooks/useJobDocsIframe';
import { resolveJobDocsContext } from 'lib/jobDocs/resolveJobDocsContext';
import { useScrimLayer } from 'lib/contexts/ScrimContext';
import useCurrentWorkOrderOpRecord from 'pages/SelectJob/SelectWorkOrderOp/hooks/useCurrentWorkOrderOpRecord';
import {
  MaterialsLog,
  MaterialPickingWizard,
} from 'components/MaterialPicking/v2';

import NoOrderOpContent from './NoOrderOpContent';
import OrderOpContent from './OrderOpContent';
import PickingOrderOpContent, { OrderOpTop } from './PickingOrderOpContent';
import { ContentContainer, FlexContentContainer } from './styled';

const DOCUMENTS_EMBED_ZONE_ID = 'jobinfoerp-documents-zone';

// This overview is a routed page, not an MMDrawer, but it must LOOK (and
// enter) like one (Figma: Current Job Modal) — same top inset, rounded top
// corners, shadow and slide-up entrance, over a dimmed backdrop. The chrome
// tokens are shared with MMDrawer so the two surfaces can never drift apart.
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${zLayer.overlay};
  display: flex;
  flex-direction: column;
  background: rgba(26, 32, 44, 0.65);
  animation: ${fadeIn} 0.2s ease-out;
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  margin-top: ${DRAWER_TOP_INSET};
  background: ${palette.White};
  border-radius: ${DRAWER_TOP_RADIUS} ${DRAWER_TOP_RADIUS} 0 0;
  box-shadow: ${DRAWER_SHADOW};
  overflow: hidden;
  /* Matches the Chakra bottom-drawer entrance so this drawer-like page and
     the real drawers feel identical. */
  animation: ${slideUp} 0.3s cubic-bezier(0, 0, 0.2, 1);
`;

// Figma modal header: 72px white bar, 1px Grey/400 bottom border,
// 24px left / 16px right padding.
const Header = styled.header`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
  height: 72px;
  padding: 0 1rem 0 1.5rem;
  background: ${palette.White};
  border-bottom: 1px solid ${palette.Grey400};
`;

// Figma: title is Noto Sans 400 (regular), 20px/24px, Grey/700.
const HeaderTitle = styled.h5`
  flex: 1;
  min-width: 0;
  margin: 0;
  font-size: 20px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.5px;
  color: ${palette.Grey700};
`;

// Figma: 40px basic icon button with the regular-weight 24px times glyph.
const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: ${palette.Grey700};
  cursor: pointer;

  &:hover {
    background: ${palette.Grey200};
  }

  &:focus-visible {
    outline: 2px solid ${palette.Blue500};
    outline-offset: 2px;
  }
`;

const TabBar = styled.div`
  display: flex;
  align-items: center;
  border-bottom: 1px solid ${palette.Grey400};
  padding: 0 0.5rem;
  flex-shrink: 0;
`;

const TabBarSpacer = styled.div`
  flex: 1;
`;

const TabItem = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 3rem;
  min-width: 3.5rem;
  padding: 0 0.25rem;
  gap: 0.8125rem;
  border: none;
  background: none;
  cursor: pointer;
  overflow: hidden;
`;

const TabLabel = styled.div`
  flex: 1;
  display: flex;
  align-items: flex-start;
  padding-top: 1rem;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ active }) => {
    return active ? palette.Grey700 : palette.Grey600;
  }};
  white-space: nowrap;
`;

const TabUnderline = styled.div`
  height: 3px;
  width: 100%;
  background: ${palette.Indigo500};
  border-radius: 4px 4px 0 0;
  opacity: ${({ active }) => {
    return active ? 1 : 0;
  }};
  flex-shrink: 0;
`;

const AddMaterialsButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  height: 2rem;
  padding: 0 0.75rem;
  background: ${palette.White};
  border: 1px solid ${palette.Grey400};
  border-radius: 0.25rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${palette.Grey700};
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: ${palette.Grey100};
  }
`;

const TabContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const PickingContent = ({
  currentWorkOrderOp,
  isSetupMode,
  wizardOpen,
  activeTab,
  setActiveTab,
  onAddMaterials,
  onWizardDone,
  onWizardClose,
}) => {
  if (wizardOpen) {
    return (
      <MaterialPickingWizard
        wooId={currentWorkOrderOp?.workOrderOperationRef}
        phase={isSetupMode ? 'setup' : 'production'}
        mode="add-materials"
        onComplete={onWizardDone}
        onBack={onWizardClose}
        onClose={onWizardClose}
      />
    );
  }

  return (
    <FlexContentContainer>
      <OrderOpTop />

      <TabBar role="tablist">
        <TabItem
          type="button"
          role="tab"
          aria-selected={activeTab === 'laborTickets'}
          onClick={() => {
            return setActiveTab('laborTickets');
          }}
        >
          <TabLabel active={activeTab === 'laborTickets'}>
            Labor Tickets
          </TabLabel>
          <TabUnderline active={activeTab === 'laborTickets'} />
        </TabItem>
        <TabItem
          type="button"
          role="tab"
          aria-selected={activeTab === 'materialsLog'}
          onClick={() => {
            return setActiveTab('materialsLog');
          }}
        >
          <TabLabel active={activeTab === 'materialsLog'}>
            Materials Log
          </TabLabel>
          <TabUnderline active={activeTab === 'materialsLog'} />
        </TabItem>
        <TabBarSpacer />
        <AddMaterialsButton type="button" onClick={onAddMaterials}>
          <Icon icon={plus} size="0.875rem" />
          Add materials
        </AddMaterialsButton>
      </TabBar>

      <TabContent>
        {activeTab === 'laborTickets' && <PickingOrderOpContent />}
        {activeTab === 'materialsLog' && (
          <MaterialsLog wooId={currentWorkOrderOp?.workOrderOperationRef} />
        )}
      </TabContent>
    </FlexContentContainer>
  );
};

PickingContent.propTypes = {
  currentWorkOrderOp: PropTypes.object,
  isSetupMode: PropTypes.bool,
  wizardOpen: PropTypes.bool.isRequired,
  activeTab: PropTypes.oneOf(['laborTickets', 'materialsLog']).isRequired,
  setActiveTab: PropTypes.func.isRequired,
  onAddMaterials: PropTypes.func.isRequired,
  onWizardDone: PropTypes.func.isRequired,
  onWizardClose: PropTypes.func.isRequired,
};

PickingContent.defaultProps = {
  currentWorkOrderOp: null,
  isSetupMode: false,
};

const JobInfoERP = ({
  handleOnClose = (history, machine, tab) => {
    // The overlay is nested under home/:tab — closing returns to the SAME
    // tab. A missing tab (stale un-nested URL) falls back to the home
    // redirect, which picks the default tab.
    return history.push({
      pathname: tab
        ? routes.machineIdHomeTabPath(machine.id, tab)
        : routes.machineIdHomePath(machine.id),
    });
  },
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { tab } = useParams();
  // This page's Backdrop dims the screen — register it as a scrim layer so
  // anything stacking above (e.g. the View Documents drawer) skips its own
  // dim and the fade keeps this backdrop's transparency.
  useScrimLayer(true);

  const job = useSelector(getLatestJob);
  const machine = useSelector(getMachine);

  const currentWorkOrderOp = useSelector(getCurrentWorkOrderOp);
  const isSetupMode = useSelector(getIsSetupModeActive);
  const materialPickingEnabled = useSelector(getMaterialPickingEnabled);

  const [activeTab, setActiveTab] = useState('laborTickets');
  // The WOO the Add materials wizard was opened for. The wizard shows only while
  // that WOO is still current, so a change of WOO closes it rather than carrying
  // its selections over to another operation.
  const [wizardWooRef, setWizardWooRef] = useState(null);
  const showPicking =
    materialPickingEnabled && Boolean(currentWorkOrderOp?.workOrderId);
  const currentWooRef = currentWorkOrderOp?.workOrderOperationRef ?? null;
  const wizardOpen =
    showPicking &&
    wizardWooRef != null &&
    String(wizardWooRef) === String(currentWooRef);

  useEffect(() => {
    if (wizardWooRef != null && !wizardOpen) {
      setWizardWooRef(null);
    }
  }, [wizardWooRef, wizardOpen]);

  const handleAddMaterials = useCallback(() => {
    setWizardWooRef(currentWooRef);
  }, [currentWooRef]);

  const handleWizardDone = useCallback(() => {
    setWizardWooRef(null);
    setActiveTab('materialsLog');
  }, []);

  const activityMode = useSelector(getLatestActivityMode);
  const isAPMEnabled = useSelector(getIsAPMEnabled);
  const isApmERPJobFlow = useSelector(getIsApmERPJobFlow);
  const { currentWorkOrderOpRecord } = useCurrentWorkOrderOpRecord({
    workOrderOperationRef: currentWorkOrderOp?.workOrderOperationRef,
  });
  const jobDocsContext = resolveJobDocsContext(currentWorkOrderOp, { job });
  const { partNumber, operationId } = jobDocsContext;
  // APM's current op carries no part operation ref (it's on the WOO record),
  // and APM's latest job ref never describes the running operation.
  let { partOperationRef } = jobDocsContext;
  if (isApmERPJobFlow && currentWorkOrderOp) {
    partOperationRef =
      currentWorkOrderOpRecord?.matchingPartOperations?.[0]?.partOperationRef;
  } else if (isAPMEnabled && !currentWorkOrderOp) {
    partOperationRef = undefined;
  }
  const {
    showSection: showJobDocsSection,
    gridUrl: jobDocsGridUrl,
    gridPath: jobDocsGridPath,
    missingPartOperationRef,
  } = useJobDocsIframe({
    partNumber,
    operationId,
    partOperationRef,
    activityMode,
  });

  return (
    <Backdrop id="job-details-page">
      <Panel>
        <Header>
          <HeaderTitle>
            {!currentWorkOrderOp
              ? `${job.name} | ${t('Operation overview')}`
              : t('Current job')}
          </HeaderTitle>
          <CloseButton
            type="button"
            aria-label={t('Close')}
            onClick={() => {
              handleOnClose(history, machine, tab);
            }}
          >
            {/* falTimes (light) matches the docs drawer's DrawerHeaderBar X. */}
            <Icon icon={falTimes} size="1.5rem" />
          </CloseButton>
        </Header>
        {/* Material picking needs a work order operation to pick against;
            otherwise the page is unchanged from before the picking redesign. */}
        {showPicking ? (
          <PickingContent
            currentWorkOrderOp={currentWorkOrderOp}
            isSetupMode={isSetupMode}
            wizardOpen={wizardOpen}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            onAddMaterials={handleAddMaterials}
            onWizardDone={handleWizardDone}
            onWizardClose={() => {
              return setWizardWooRef(null);
            }}
          />
        ) : (
          <ContentContainer>
            {currentWorkOrderOp?.workOrderId ? (
              <OrderOpContent />
            ) : (
              <NoOrderOpContent job={job} />
            )}
          </ContentContainer>
        )}
        {/* Figma: the View Documents footer is the bottom bar of the overview —
            the single job-docs entry point on this screen. */}
        {showJobDocsSection && !wizardOpen && (
          <JobDocsFooter
            embedZoneId={DOCUMENTS_EMBED_ZONE_ID}
            gridUrl={jobDocsGridUrl}
            gridPath={jobDocsGridPath}
            partNumber={partNumber}
            operationId={operationId}
            missingPartOperationRef={missingPartOperationRef}
          />
        )}
      </Panel>
    </Backdrop>
  );
};

JobInfoERP.propTypes = {
  handleOnClose: PropTypes.func,
};

export default JobInfoERP;
